/**
 * The evidence a capability is admitted on: evaluation suites and their
 * readiness, evaluation runs, their cases, comparison and review, and the
 * composed runs that evaluate a selection bundle.
 */

import type {
  LaunchAssessmentResponse,
  ReviewAssessmentRequest,
  ReviewAssessmentResponse,
  BundleComparisonResponse,
  BundleRun,
  BundleRunFilters,
  BundleRunListResponse,
  CaseResultParams,
  ComparisonResponse,
  CreateBundleRunRequest,
  CreateRunRequest,
  EvaluationCaseResultListResponse,
  EvaluationRun,
  EvaluationRunFilters,
  EvaluationRunListResponse,
  EvaluationSuite,
  EvaluationSuiteListResponse,
  Qualification,
  QualificationReviewRequest,
  SuiteFilters,
  SuiteReadiness,
  SuiteReadinessCarryForwardRequest,
} from '@/types/modelCapabilities'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'
import { BASE } from './modelCapabilityPaths'

export const modelCapabilityEvidence = {
  assessLaunch: async (
    body: CreateRunRequest
  ): Promise<LaunchAssessmentResponse> => {
    const response = await apiClient.post<LaunchAssessmentResponse>(
      `${BASE}/run-assessments/`,
      body
    )
    return response.data
  },

  assessReview: async (
    runId: string,
    body: ReviewAssessmentRequest
  ): Promise<ReviewAssessmentResponse> => {
    const response = await apiClient.post<ReviewAssessmentResponse>(
      `${BASE}/runs/${runId}/review-assessment/`,
      body
    )
    return response.data
  },

  listSuites: async (
    filters: SuiteFilters = {}
  ): Promise<EvaluationSuiteListResponse> => {
    const response = await apiClient.get<EvaluationSuiteListResponse>(
      `${BASE}/suites/`,
      { params: filters }
    )
    return response.data
  },

  getSuite: async (suiteId: string): Promise<EvaluationSuite> => {
    const response = await apiClient.get<EvaluationSuite>(
      `${BASE}/suites/${suiteId}/`
    )
    return response.data
  },

  // If-Match is REQUIRED: this is the write that lets a suite gate production
  // admission, so a stale version is refused (412) rather than merged.
  recordReadiness: async (
    suiteId: string,
    body: SuiteReadiness,
    ifMatch: number
  ): Promise<EvaluationSuite> => {
    const response = await apiClient.post<EvaluationSuite>(
      `${BASE}/suites/${suiteId}/readiness/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // If-Match is REQUIRED and is the *target's* metadata_version, for
  // recordReadiness's reason: this opens the same gate. A 409 is either a
  // precondition the pair fails or a corpus that differs beyond the planner
  // stamp, whose body (`SuiteIdentityMismatch`) lists every difference.
  carryForwardReadiness: async (
    suiteId: string,
    body: SuiteReadinessCarryForwardRequest,
    ifMatch: number
  ): Promise<EvaluationSuite> => {
    const response = await apiClient.post<EvaluationSuite>(
      `${BASE}/suites/${suiteId}/readiness/carry-forward/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listRuns: async (
    filters: EvaluationRunFilters = {}
  ): Promise<EvaluationRunListResponse> => {
    const response = await apiClient.get<EvaluationRunListResponse>(
      `${BASE}/runs/`,
      { params: filters }
    )
    return response.data
  },

  getRun: async (runId: string): Promise<EvaluationRun> => {
    const response = await apiClient.get<EvaluationRun>(
      `${BASE}/runs/${runId}/`
    )
    return response.data
  },

  listRunCases: async (
    runId: string,
    params: CaseResultParams = {}
  ): Promise<EvaluationCaseResultListResponse> => {
    const response = await apiClient.get<EvaluationCaseResultListResponse>(
      `${BASE}/runs/${runId}/cases/`,
      { params }
    )
    return response.data
  },

  // 202 — the row is queued and nothing has been measured yet, so this reply
  // is the run as accepted, never a result.
  launchRun: async (body: CreateRunRequest): Promise<EvaluationRun> => {
    const response = await apiClient.post<EvaluationRun>(`${BASE}/runs/`, body)
    return response.data
  },

  cancelRun: async (runId: string): Promise<EvaluationRun> => {
    const response = await apiClient.post<EvaluationRun>(
      `${BASE}/runs/${runId}/cancel/`
    )
    return response.data
  },

  // Repeated `run_id` keys, not axios' bracket-array form: the route binds a
  // list bounded at two to six ids and answers 422 outside that.
  compareRuns: async (runIds: string[]): Promise<ComparisonResponse> => {
    const response = await apiClient.get<ComparisonResponse>(
      `${BASE}/runs/compare/`,
      {
        params: { run_id: runIds },
        paramsSerializer: repeatedKeyParamsSerializer,
      }
    )
    return response.data
  },

  // If-Match is REQUIRED and is the *run's* metadata_version: what two
  // administrators race over is which of them reviews this evidence.
  reviewRun: async (
    runId: string,
    body: QualificationReviewRequest,
    ifMatch: number
  ): Promise<Qualification> => {
    const response = await apiClient.post<Qualification>(
      `${BASE}/runs/${runId}/review/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listBundleRuns: async (
    filters: BundleRunFilters = {}
  ): Promise<BundleRunListResponse> => {
    const response = await apiClient.get<BundleRunListResponse>(
      `${BASE}/bundle-runs/`,
      { params: filters }
    )
    return response.data
  },

  getBundleRun: async (bundleRunId: string): Promise<BundleRun> => {
    const response = await apiClient.get<BundleRun>(
      `${BASE}/bundle-runs/${bundleRunId}/`
    )
    return response.data
  },

  launchBundleRun: async (body: CreateBundleRunRequest): Promise<BundleRun> => {
    const response = await apiClient.post<BundleRun>(
      `${BASE}/bundle-runs/`,
      body
    )
    return response.data
  },

  compareBundleRuns: async (
    bundleRunIds: string[]
  ): Promise<BundleComparisonResponse> => {
    const response = await apiClient.get<BundleComparisonResponse>(
      `${BASE}/bundle-runs/compare/`,
      {
        params: { bundle_run_id: bundleRunIds },
        paramsSerializer: repeatedKeyParamsSerializer,
      }
    )
    return response.data
  },
}
